import React from "react";
import { Box } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { EcosystemPartner } from "./ecosystemApi";
import { CATEGORY_COLORS, ChartCard, GREEN, PRIMARY } from "./parts";

const truncate = (value: string, max = 22) =>
  value.length > max ? `${value.slice(0, max - 1)}…` : value;

/**
 * The two charts the unified report keeps: top partners (completed vs the rest
 * of their pipeline) and the category split. Adapted from EcosystemReport.
 */
const ReferralCharts: React.FC<{ partners: EcosystemPartner[] }> = ({
  partners,
}) => {
  const top = [...partners]
    .sort((a, b) => b.total - a.total)
    .slice(0, 10)
    .map((partner) => ({
      name: truncate(partner.name),
      fullName: partner.name,
      Completed: partner.byStatus["completed"] || 0,
      "Not completed": Math.max(
        0,
        partner.total - (partner.byStatus["completed"] || 0),
      ),
    }))
    // Recharts draws a vertical bar chart bottom-up; reverse so the biggest
    // partner sits at the top.
    .reverse();

  const categoryTotals = new Map<string, number>();
  partners.forEach((partner) => {
    categoryTotals.set(
      partner.category,
      (categoryTotals.get(partner.category) || 0) + partner.total,
    );
  });
  const categoryData = Array.from(categoryTotals.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  return (
    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
      <ChartCard
        title="Top 10 Partners"
        subtitle="Referrals received, split into completed and everything still in the pipeline"
        height={Math.max(280, top.length * 30 + 60)}
      >
        <BarChart
          data={top}
          layout="vertical"
          margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            formatter={(value: any, name: any) => [value, name]}
            labelFormatter={(label: any, payload: any) =>
              payload && payload[0]
                ? payload[0].payload.fullName || label
                : label
            }
          />
          <Legend />
          <Bar
            dataKey="Completed"
            stackId="pipeline"
            fill={GREEN}
            isAnimationActive={false}
          />
          <Bar
            dataKey="Not completed"
            stackId="pipeline"
            fill="#d5c2ce"
            isAnimationActive={false}
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ChartCard>

      <ChartCard
        title="Referrals by Category"
        subtitle='Answers "how many clients were connected to funding resources"'
        height={320}
      >
        <PieChart>
          <Pie
            data={categoryData}
            dataKey="value"
            nameKey="name"
            innerRadius={55}
            outerRadius={95}
            paddingAngle={2}
            label={(entry: any) => entry.value}
            isAnimationActive={false}
          >
            {categoryData.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={
                  index === 0
                    ? PRIMARY
                    : CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                }
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ChartCard>
    </Box>
  );
};

export default ReferralCharts;
